import React, { useState } from "react";
import { DraggableScrollContainer } from "../../atom/DraggableScrollContainer";
import { ExamsHeading } from "./ExamsHeading";
import { EnrolledExams } from "./EnrolledExams";
import { RecentExams } from "./RecentExams";
import { OtherUpcomingExams } from "./OtherUpcomingExams";
import { ExamsFilter, ParticipantExam } from "../../../types/components";
import { useEnrolledExams } from "../../../utils/hooks/data/useEnrolledExams";
import { useResource } from "../../../services";
import { today } from "../../../utils/date";
import { useUserContext } from "../../../utils/contexts/UserProvider";
import { canUserDoSmallExam } from "../../../utils/user";
import { createResourceUrl } from "../../../utils/data";

export const Exams = () => {
  const [filter, setFilter] = useState<ExamsFilter>("all");
  const { user } = useUserContext();
  const { data: enrolledExams, mutate: mutateEnrolledExams } = useEnrolledExams();
  const { data: recentExams } = useResource<ParticipantExam[]>(
    createResourceUrl("/common/participant-exam", {
      include: [
        "exam",
        "exam.exam_type",
        "exam.responsible_persons",
        "exam.responsible_persons.responsible_person",
        "certificate",
      ],
      filters: {
        "eq:participant_id": user?.id,
        "eq:is_completed": 1,
        "lteDate:exam.date_to": today,
        "userInsuranceCompany:1": "exam.insurance_company.agents",
      },
      sort: "exam.date_to",
    }),
    user,
  );
  const { data: upcomingExams, mutate: mutateUpcomingExams } = useResource<
    ParticipantExam["exam"][]
  >(
    createResourceUrl("/common/exam", {
      include: ["exam_type", "responsible_persons", "responsible_persons.responsible_person"],
      filters: {
        "gteDateTime:date_to": today,
        "userInsuranceCompany:1": "insurance_company.agents",
        "notEnrolledExams:1": 1,
        "eq:is_archived": 0,
        "eq:status.code": "scheduled",
        "eq:exam_type.code": !canUserDoSmallExam(user) ? "big" : "small",
      },
      sort: "date_from",
    }),
    user,
  );

  const filterComponents: Record<ExamsFilter, React.ReactElement> = {
    all: <></>,
    enrolled: <EnrolledExams data={enrolledExams} />,
    recent: <RecentExams data={recentExams} />,
    upcoming: (
      <OtherUpcomingExams
        data={upcomingExams}
        mutateEnrolledExams={mutateEnrolledExams}
        mutateUpcomingExams={mutateUpcomingExams}
      />
    ),
  };

  return (
    <div className="flex flex-col flex-1 items-center w-full">
      <div className="w-full overflow-hidden flex flex-col h-full">
        <DraggableScrollContainer className="m-[20px] overflow-y-auto select-none ml-[8px] bg-gray-100 h-full">
          <ExamsHeading filter={filter} setFilter={setFilter} />
          {filter === "all" ? (
            <>
              <EnrolledExams data={enrolledExams} />
              <RecentExams data={recentExams} />
              {!user?.is_successfully_passed_exam && (
                <OtherUpcomingExams
                  data={upcomingExams}
                  mutateEnrolledExams={mutateEnrolledExams}
                  mutateUpcomingExams={mutateUpcomingExams}
                />
              )}
            </>
          ) : (
            <>{filterComponents[filter]}</>
          )}
        </DraggableScrollContainer>
      </div>
    </div>
  );
};
